import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../lib/SEO";
import { useCart, effectiveUnitPrice } from "../lib/CartContext";

const catSlug = (name) => (name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [qty, setQty] = useState(1);
    const [option, setOption] = useState("");

    useEffect(() => {
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                if (Array.isArray(r.data.options) && r.data.options.length > 0) {
                    setOption(r.data.options[0]);
                }
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    const addToCart = () => {
        if (!product) return;
        const item = option
            ? { ...product, name: `${product.name} — ${option}` }
            : product;
        addItem(item, qty);
        toast.success(`${product.name} added to cart`);
    };

    const buyNow = () => {
        addToCart();
        navigate("/cart");
    };

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center">
                <h2 className="font-display text-3xl mb-4">Product not found</h2>
                <Link to="/shop" className="btn-pink mt-4">Back to shop</Link>
            </main>
        );
    }

    if (!product) {
        return <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center text-ink-soft">Loading…</main>;
    }

    return (
        <main data-testid="product-detail-page">
            <SEO
                title={`${product.name} — Cake Central`}
                description={product.short_description}
                image={product.image_url}
                path={`/shop/product/${product.slug}`}
            />

            <section className="pt-24 md:pt-[10rem] pb-16 bg-[#FFF7FB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to={`/shop/${catSlug(product.category)}`}
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-8 transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to {product.category}
                    </Link>

                    <div className="grid md:grid-cols-2 gap-10 md:gap-16">
                        <div>
                            <div className="aspect-[4/5] overflow-hidden bg-white">
                                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-blush mb-3">{product.category}</p>
                            <h1 className="font-display text-4xl md:text-5xl text-ink mb-3 leading-none">
                                {product.name}
                            </h1>
                            {(() => {
                                const perUnit = effectiveUnitPrice(product.price, product.price_tiers, qty);
                                const lineTotal = perUnit * qty;
                                const showsSavings = perUnit < Number(product.price);
                                return (
                                    <div className="mb-6">
                                        <p className="font-display text-3xl text-ink">
                                            ${lineTotal.toFixed(2)} <span className="text-sm text-ink-soft tracking-[0.2em] uppercase">AUD</span>
                                        </p>
                                        {qty > 1 && (
                                            <p className="text-xs text-ink-soft mt-1">
                                                ${perUnit.toFixed(2)} each × {qty}
                                            </p>
                                        )}
                                        {showsSavings && (
                                            <p className="text-xs text-blush tracking-[0.15em] uppercase mt-1">Bulk discount applied</p>
                                        )}
                                    </div>
                                );
                            })()}
                            {Array.isArray(product.price_tiers) && product.price_tiers.length > 0 && (
                                <div className="mb-6 p-4 bg-blush-pale/40 border border-blush/20">
                                    <p className="text-[10px] tracking-[0.3em] uppercase text-ink-soft mb-2">Buy more, save more</p>
                                    <ul className="space-y-1 text-sm">
                                        <li>1 = ${Number(product.price).toFixed(2)}</li>
                                        {product.price_tiers.map((t, i) => (
                                            <li key={i}>
                                                {t.qty} = ${Number(t.price).toFixed(2)}{" "}
                                                <span className="text-xs text-ink-soft">(${(Number(t.price) / Number(t.qty)).toFixed(2)} each)</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <p className="text-ink-soft leading-relaxed mb-8 whitespace-pre-line">
                                {product.long_description}
                            </p>

                            <div className="h-px bg-[#EAE3DE] mb-8" />

                            {Array.isArray(product.options) && product.options.length > 0 && (
                                <div className="mb-6">
                                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-3">
                                        Flavour / Option
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {product.options.map((opt) => (
                                            <button
                                                key={opt}
                                                onClick={() => setOption(opt)}
                                                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                                                    option === opt
                                                        ? "bg-blush text-white border-blush"
                                                        : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                                                }`}
                                                data-testid={`option-${opt}`}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mb-6">
                                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-3">
                                    Quantity
                                </label>
                                <div className="inline-flex items-center border border-[#EAE3DE] bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                                        className="px-3 py-2 text-ink hover:text-blush"
                                        aria-label="Decrease"
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span className="px-5 font-display text-lg min-w-[2rem] text-center">{qty}</span>
                                    <button
                                        type="button"
                                        onClick={() => setQty((q) => q + 1)}
                                        className="px-3 py-2 text-ink hover:text-blush"
                                        aria-label="Increase"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <button onClick={addToCart} className="btn-pink btn-pink-outline" data-testid="add-to-cart-btn">
                                    <ShoppingBag size={14} /> Add to cart
                                </button>
                                <button onClick={buyNow} className="btn-pink" data-testid="buy-now-btn">
                                    Buy now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
